import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon, Close as CloseIcon, CompareArrows as CompareArrowsIcon, Edit as EditIcon } from "@mui/icons-material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  fetchSimInvestmentBreakdown,
  fetchSimFactors,
  fetchSimInvestmentDetail,
  fetchSimInvestmentComparison,
  fetchAvailableSims,
  fetchAvailableDates,
} from "../../../services/RiskMonitorAxios";
import GVarLoader from "../../ui/GVarLoader";
import { FactorPanel, FACTOR_INDEX_MAP } from "./FactorPanel";
import DownloadIcon from "@mui/icons-material/Download";

// Beta is regressed against the log-return of VIX, not VIX's level like the
// simulation-factor panel — so the Beta table's hover tooltip should read
// ln(VIX) for that row specifically.
const BETA_FACTOR_INDEX_MAP = { ...FACTOR_INDEX_MAP, vix: "ln(VIX Index)" };

function formatCurrency(value) {
  if (value == null || isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value, decimals = 2) {
  if (value == null || isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

const DRIVER_LABELS = {
  // Shared / unpaired
  day_count_interval:"Days",
  net_spread_ROR_monitor: "Net IRR Monitor (%)",
  net_spread_ROR_used: "Net IRR Used (%)",
  net_spread_ROR: "Net IRR (%)",
  priced_currency:"Currency",
  spac_scalar: "SPAC Scalar",
  stock_returns: "Stock Return",
  strike: "Strike",
  dividend_yield: "Dividend Yield",
  time_to_maturity_years: "Time to Maturity (Years)",
  comp_ticker: "Comp Ticker",
  acquiror_ticker: "Acquiror Ticker",
  par_spread: "Par Spread",
  borrow_cost: "Borrow Cost",
  option_stock_ticker: "Option Stock Ticker",
  rec_rate: "Recovery Rate",
  cpn: "Coupon",
  net_spread_downsideRisk_bond: "Net Spread (Merger Arb Bond)",
  imp_prob_after_finance: "Implied Prob After Finance",
  upside_price: "Upside Price",
  ctd_bond_id: "CTD Bond ID",
  // Stem labels (after stripping _base / _simulated / _sims)
  investment_code: "Investment Description",
  model:"VaR Model",
  ytw:"YTW",
  spx_shock: "SPX Shock",
  stock: "Stock Price",
  stock_sims: "Stock Price",
  sim_prices: "Simulated Price",
  imp_prob: "Implied Probability",
  target_downside: "Target Downside",
  deal_break_spread: "Deal-Break Spread",
  deal_break: "Deal-Break",
  target_dealbreak: "Target Deal-Break",
  net_spread: "Net Spread",
  gross_spread: "Gross Spread",
  comp_ret: "Comp Return",
  total_deal_value: "Total Deal Value",
  acquiror_price: "Acquiror Price",
  vol: "Volatility",
  cvt_credit_spread: "Convertible Credit Spread",
  cds_spread: "CDS Spread",
  rate: "Rate",
  iv: "Implied Vol",
  futures_price: "Futures Price",
  deal_brk_px: "Deal-Break Price",
};

const SIM_SIDE_SUFFIXES = ["_simulated", "_sims", "_sim"];

/** Strip _base / _simulated / _sims / _sim → stem, or null if not sided. */
function driverStem(key) {
  if (!key) return null;
  if (key.endsWith("_base")) return key.slice(0, -"_base".length);
  if (key.endsWith("_simulated")) return key.slice(0, -"_simulated".length);
  if (key.endsWith("_sims")) return key.slice(0, -"_sims".length);
  if (key.endsWith("_sim")) return key.slice(0, -"_sim".length);
  if (key === "sim_prices") return "sim_prices";
  return null;
}

function isSimSideKey(key) {
  if (!key) return false;
  if (key === "sim_prices") return true;
  return SIM_SIDE_SUFFIXES.some((s) => key.endsWith(s));
}

function isBaseSideKey(key) {
  return Boolean(key && key.endsWith("_base"));
}

function driverDisplayLabel(stemOrKey) {
  if (!stemOrKey) return "";
  if (DRIVER_LABELS[stemOrKey]) return DRIVER_LABELS[stemOrKey];
  const stem = driverStem(stemOrKey) || stemOrKey;
  if (DRIVER_LABELS[stem]) return DRIVER_LABELS[stem];
  return humanizeKey(stem);
}

/**
 * Split driver list into:
 * - shared: no base/sim side in the name
 * - paired: { stem, label, baseValue, simValue }
 * Prefers _simulated over legacy _sims/_sim for the sim side.
 */
function categorizeDriverList(drivers) {
  const list = Array.isArray(drivers) ? drivers : [];
  const byKey = new Map(list.map((d) => [d.key, d.value]));
  const shared = [];
  const pairedByStem = new Map();

  for (const { key, value } of list) {
    if (isBaseSideKey(key)) {
      const stem = driverStem(key);
      const entry = pairedByStem.get(stem) || { stem, label: driverDisplayLabel(stem), baseValue: undefined, simValue: undefined };
      entry.baseValue = value;
      pairedByStem.set(stem, entry);
    } else if (isSimSideKey(key)) {
      const stem = driverStem(key);
      // Map legacy sims stems onto the same conceptual stem when possible
      // e.g. imp_prob_sims → imp_prob (pairs with imp_prob_base)
      const normalizedStem = stem === "sim_prices" ? "sim_prices" : stem;
      const entry = pairedByStem.get(normalizedStem) || {
        stem: normalizedStem,
        label: driverDisplayLabel(normalizedStem),
        baseValue: undefined,
        simValue: undefined,
      };
      // Prefer _simulated if already set from a weaker suffix? First write wins unless simulated
      if (entry.simValue === undefined || key.endsWith("_simulated")) {
        entry.simValue = value;
      }
      pairedByStem.set(normalizedStem, entry);
    } else {
      shared.push({
        key,
        label: driverDisplayLabel(key),
        value,
      });
    }
  }

  return {
    sharedFields: shared,
    pairedFields: Array.from(pairedByStem.values()),
  };
}

function humanizeKey(key) {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Determine if an instrument is a bond or convertible. */
function isBondOrConvertible(row) {
  if (!row) return false;
  // Check investment_type_code if available (from detail response)
  const typeCode = row?.investment_type_code;
  if (typeCode) {
    const upperType = String(typeCode).toUpperCase().trim();
    const bondsAndConverts = ["BD", "BDTRS", "CB", "GB", "CV"];
    return bondsAndConverts.includes(upperType);
  }
  // Fallback: check strategy_name for CVT (convertible) or BOND patterns
  const strategyName = row?.strategy_name || "";
  return strategyName.toUpperCase().includes("CVT") || strategyName.toUpperCase().includes("BOND");
}

/** Get the appropriate investment code based on type. */
function getDisplayInvestmentCode(row) {
  if (isBondOrConvertible(row)) {
    // For bonds and convertibles, use investment_code if available, otherwise investment_code_2
    return row?.investment_code || row?.investment_code_2;
  }
  return row?.investment_code_2;
}

function formatDriverValue(value) {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number") {
    return Math.abs(value) >= 1000 ? formatNumber(value, 2) : formatNumber(value, 4);
  }
  return String(value);
}

const CORE_FIELD_CONFIG = [
  { key: "strategy_name", label: "Strategy", format: (v) => v },
  { key: "investment_code", label: "Investment Description", format: (v) => v },
  { key: "investment_type_code", label: "Investment Type", format: (v) => v },
  { key: "priced_currency", label: "Currency", format: (v) => v },
  { key: "quantity", label: "Quantity", format: (v) => formatNumber(v, 0) },
  { key: "priced_at", label: "Base", format: (v) => formatNumber(v, 4) },
  { key: "sim_price", label: "Simulated", format: (v) => formatNumber(v, 4) },
  { key: "multiplier", label: "Multiplier", format: (v) => formatNumber(v, 2) },
  { key: "fx_rate", label: "FX Rate", format: (v) => formatNumber(v, 4) },
  { key: "pnl", label: "PnL", format: (v) => formatCurrency(v) },
];

const DETAIL_SECTION_TITLE_SX = {
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#006842",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const DETAIL_KV_CELL_SX = {
  py: 0.45,
  px: 1,
  borderBottom: "1px solid rgba(0,0,0,0.06)",
  fontSize: "0.78rem",
  lineHeight: 1.3,
};

/** Compact 2-column key/value card used by single-sim and compare detail popups. */
function DetailKvSection({ title, fields }) {
  if (!fields || fields.length === 0) return null;
  return (
    <Box
      sx={{
        mb: 2,
        border: "1px solid rgba(0,104,66,0.12)",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#fafbfa",
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.6,
          bgcolor: "rgba(0,104,66,0.07)",
          borderBottom: "1px solid rgba(0,104,66,0.12)",
        }}
      >
        <Typography sx={DETAIL_SECTION_TITLE_SX}>{title}</Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 0,
          rowGap: 0,
        }}
      >
        {fields.map(({ label, value }, idx) => (
          <Box
            key={`${label}-${idx}`}
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1.5,
              ...DETAIL_KV_CELL_SX,
              borderRight: {
                sm: idx % 2 === 0 ? "1px solid rgba(0,0,0,0.06)" : "none",
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "text.secondary",
                flexShrink: 0,
              }}
            >
              {label}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "text.primary",
                textAlign: "right",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function InvestmentDetailDialog({
  open, onClose, loading, error, data, investmentCode, strategyType, simNumber, sourceRow,
}) {
  /**
   * Three kinds of risk parameters:
   * 1. Simulated — keys ending _simulated / _sims / _sim
   * 2. Base case — keys ending _base
   * 3. Static — everything else (no base/simulated tag)
   *
   * Display: static list on top, then side-by-side BASE | SIMULATED table
   * (paired by stem; missing side shows —).
   */
  const categorizeFields = () => {
    if (!data) return { positionFields: [], staticFields: [], pairedFields: [] };

    const positionKeys = ["strategy_name", "investment_code", "investment_type_code", "priced_currency","quantity", "multiplier", "fx_rate", "pnl"];
    const positionFields = positionKeys
      // Prefer the original row (already portfolio-scoped) over the
      // detail fetch, which doesn't honor the portfolio filter.
      .filter((key) => sourceRow?.[key] !== undefined || data.core?.[key] !== undefined)
      .map((key) => {
        const config = CORE_FIELD_CONFIG.find((f) => f.key === key);
        const value = sourceRow?.[key] !== undefined ? sourceRow[key] : data.core[key];
        return {
          label: config?.label || key,
          value: config?.format(value),
        };
      });

    const { sharedFields: staticDrivers, pairedFields: pairedDrivers } = categorizeDriverList(data.drivers || []);
    const staticFields = staticDrivers.map((d) => ({
      label: d.label,
      value: formatDriverValue(d.value),
    }));

    // BASE | SIMULATED rows: core price + every tagged risk param stem
    const pairedFields = [];
    if (data.core?.priced_at !== undefined || data.core?.sim_price !== undefined) {
      pairedFields.push({
        label: "Price",
        baseValue: data.core?.priced_at,
        simValue: data.core?.sim_price,
      });
    }
    for (const p of pairedDrivers) {
      if (p.stem === "sim_prices") continue; // covered by Price row
      pairedFields.push({
        label: p.label,
        baseValue: p.baseValue,
        simValue: p.simValue,
      });
    }

    return { positionFields, staticFields, pairedFields };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
            {getDisplayInvestmentCode(data) || investmentCode}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            {strategyType} · Sim #{simNumber}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <GVarLoader size={120} sx={{ py: 4 }} />}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && data && (() => {
          const { positionFields, staticFields, pairedFields } = categorizeFields();
          const hasBeta = !!(data?.beta_factors && Object.keys(data.beta_factors).length > 0);
          return (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: hasBeta ? { xs: "1fr", md: "1fr 300px" } : "1fr",
                gap: 2,
                alignItems: "start",
              }}
            >
              <Box>
                <DetailKvSection title="Position" fields={positionFields} />
                <DetailKvSection title="Static Risk Parameters" fields={staticFields} />

                {pairedFields.length > 0 && (
                  <Box
                    sx={{
                      border: "1px solid rgba(0,104,66,0.12)",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.6,
                        bgcolor: "rgba(0,104,66,0.07)",
                        borderBottom: "1px solid rgba(0,104,66,0.12)",
                      }}
                    >
                      <Typography sx={DETAIL_SECTION_TITLE_SX}>Initial Value vs Simulated Value</Typography>
                    </Box>
                    <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.55, px: 1.25, fontSize: "0.78rem" } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: "#ffffff" }}>Parameter</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#e8f2ed", color: "#006842" }}>
                            INITIAL VALUE
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f5edd9", color: "#8a6d2b" }}>
                            SIMULATED VALUE
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pairedFields.map(({ label, baseValue, simValue }, idx) => (
                          <TableRow
                            key={idx}
                            sx={{ bgcolor: idx % 2 === 0 ? "#fff" : "#f7f9f8" }}
                          >
                            <TableCell sx={{ fontWeight: 600, color: "text.secondary", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                              {label}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 600,
                                bgcolor: "rgba(0,104,66,0.03)",
                                borderBottom: "1px solid rgba(0,0,0,0.05)",
                              }}
                            >
                              {formatDriverValue(baseValue)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 600,
                                bgcolor: "rgba(190,155,83,0.05)",
                                borderBottom: "1px solid rgba(0,0,0,0.05)",
                              }}
                            >
                              {formatDriverValue(simValue)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {positionFields.length === 0 && staticFields.length === 0 && pairedFields.length === 0 && !hasBeta && (
                  <Typography variant="body2" color="text.secondary">
                    No driver detail available for this simulation.
                  </Typography>
                )}
              </Box>

              {hasBeta && (
                <FactorPanel
                  factors={data.beta_factors}
                  title="Beta"
                  formatValue={(_key, value) => value.toFixed(2)}
                  indexMap={BETA_FACTOR_INDEX_MAP}
                  showAbsoluteRates={false}
                />
              )}
            </Box>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

/** One stat card in the PnL Δ attribution strip — big $ value, label, optional % chip. */
function AttributionCard({ label, value, pctLabel, accent, emphasize }) {
  const isNeg = value < 0;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        border: "1px solid",
        borderColor: emphasize ? accent : "rgba(0,0,0,0.08)",
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: emphasize ? `${accent}14` : "background.paper",
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography
          sx={{
            fontFamily: "monospace",
            fontWeight: 800,
            fontSize: "1.1rem",
            color: isNeg ? "#c53030" : "#006842",
            lineHeight: 1.2,
          }}
        >
          {formatCurrency(value)}
        </Typography>
        {pctLabel != null && (
          <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.78rem", color: "text.secondary" }}>
            {pctLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function PositionCompareSummary({
  open,
  onClose,
  investmentCode,
  baseDate,
  baseSim,
  compareDate,
  compareSim,
  strategyType,
  portfolioParam,
  row,
}) {
  const [loading, setLoading] = useState(false);
  const [baseDetail, setBaseDetail] = useState(null);
  const [compareDetail, setCompareDetail] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open || !investmentCode) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setBaseDetail(null);
    setCompareDetail(null);

    const strategyName = row?.strategy_name || null;
    // Settle independently: an investment can legitimately not exist on one
    // of the two dates (only_base / only_compare rows) — that side's 404
    // shouldn't block showing the full breakdown for the side that does
    // have data. Missing-side fields render as "—" via formatDriverValue.
    Promise.allSettled([
      fetchSimInvestmentDetail(
        baseDate, Number(baseSim), investmentCode, strategyType, portfolioParam,
        { signal: controller.signal },
        strategyName,
      ),
      fetchSimInvestmentDetail(
        compareDate, Number(compareSim), investmentCode, strategyType, portfolioParam,
        { signal: controller.signal },
        strategyName,
      ),
    ])
      .then(([baseResult, compareResult]) => {
        if (controller.signal.aborted) return;
        setBaseDetail(baseResult.status === "fulfilled" ? baseResult.value : null);
        setCompareDetail(compareResult.status === "fulfilled" ? compareResult.value : null);
        if (baseResult.status === "rejected" && compareResult.status === "rejected") {
          setError(
            baseResult.reason?.response?.data?.detail
            || baseResult.reason?.message
            || "Failed to load detail",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => { controller.abort(); };
  }, [open, investmentCode, baseDate, baseSim, compareDate, compareSim, strategyType, portfolioParam, row?.strategy_name]);

  if (!open) return null;

  const summaryFields = [
    { key: "priced_at", label: "Priced At", format: (v) => formatNumber(v, 4) },
    { key: "sim_price", label: "Sim Price", format: (v) => formatNumber(v, 4) },
    { key: "quantity", label: "Quantity", format: (v) => formatNumber(v, 0) },
  ];

  // Same row/detail-fetch priority used elsewhere in this dialog: prefer the
  // comparison row's own (portfolio-scoped) value, fall back to the per-side
  // detail fetch, and finally treat a genuinely missing side as zero.
  const fieldValue = (key, side) => {
    const raw = side === "base" ? (row?.[key] ?? baseDetail?.core?.[key]) : (row?.[`compare_${key}`] ?? compareDetail?.core?.[key]);
    return raw;
  };

  // Quantity × Price attribution of the PnL Δ (midpoint method), using the
  // per-unit "price" (sim − priced_at, converted to $ via multiplier/fx —
  // both carried through so an FX or multiplier change between dates shows
  // up as part of the price effect) so the two buckets multiply out to the
  // same $ PnL the row/summary table already shows.
  const computePnlAttribution = () => {
    const qBase = fieldValue("quantity", "base") ?? 0;
    const qCompare = fieldValue("quantity", "compare") ?? 0;

    const unitValue = (side) => {
      const pricedAt = fieldValue("priced_at", side);
      const simPrice = fieldValue("sim_price", side);
      if (pricedAt == null || simPrice == null) return 0;
      const multiplier = fieldValue("multiplier", side) ?? 1;
      const fxRate = fieldValue("fx_rate", side) ?? 1;
      return (simPrice - pricedAt) * multiplier * fxRate;
    };

    const pBase = unitValue("base");
    const pCompare = unitValue("compare");

    const dq = qBase - qCompare;
    const dp = pBase - pCompare;
    const impliedDelta = pBase * qBase - pCompare * qCompare;
    const actualDelta = (fieldValue("pnl", "base") ?? 0) - (fieldValue("pnl", "compare") ?? 0);

    const midQty = dq * (pCompare + pBase) / 2;
    const midPrice = dp * (qCompare + qBase) / 2;

    const hasData = (qBase !== 0 || qCompare !== 0) && (pBase !== 0 || pCompare !== 0);
    return { hasData, impliedDelta, actualDelta, midQty, midPrice };
  };

  // Full breakdown: Position + Static cards, then 4-col Base/Simulated table.
  const pairDrivers = () => {
    const positionCore = ["strategy_name", "investment_code", "investment_type_code", "priced_currency","quantity", "multiplier", "fx_rate", "pnl"];
    const positionFields = [];

    positionCore.forEach((key) => {
      // Prefer the comparison row's own value (portfolio-scoped) over the
      // per-side detail fetch, which doesn't honor the portfolio filter.
      const baseVal = row?.[key] ?? baseDetail?.core?.[key];
      const compareVal = row?.[`compare_${key}`] ?? compareDetail?.core?.[key];
      if (baseVal !== undefined || compareVal !== undefined) {
        const config = CORE_FIELD_CONFIG.find((f) => f.key === key);
        const format = config?.format || formatDriverValue;
        const val = baseVal ?? compareVal;
        positionFields.push({
          label: config?.label || key,
          value: typeof format === "function" ? format(val) : formatDriverValue(val),
        });
      }
    });

    const currentCats = categorizeDriverList(baseDetail?.drivers || []);
    const compareCats = categorizeDriverList(compareDetail?.drivers || []);

    // Static drivers (prefer current)
    const staticFields = [];
    const staticKeys = new Set();
    for (const d of currentCats.sharedFields) {
      staticFields.push({ label: d.label, value: formatDriverValue(d.value) });
      staticKeys.add(d.key);
    }
    for (const d of compareCats.sharedFields) {
      if (!staticKeys.has(d.key)) {
        staticFields.push({ label: d.label, value: formatDriverValue(d.value) });
      }
    }

    // Merge paired stems across current + compare into 4 columns
    const pairedByStem = new Map();
    for (const p of currentCats.pairedFields) {
      if (p.stem === "sim_prices") continue;
      pairedByStem.set(p.stem, {
        label: p.label,
        currentBase: p.baseValue,
        currentSim: p.simValue,
        compareBase: undefined,
        compareSim: undefined,
      });
    }
    for (const p of compareCats.pairedFields) {
      if (p.stem === "sim_prices") continue;
      const existing = pairedByStem.get(p.stem) || {
        label: p.label,
        currentBase: undefined,
        currentSim: undefined,
        compareBase: undefined,
        compareSim: undefined,
      };
      existing.compareBase = p.baseValue;
      existing.compareSim = p.simValue;
      pairedByStem.set(p.stem, existing);
    }

    const paired = [
      {
        label: "Price",
        currentBase: baseDetail?.core?.priced_at,
        currentSim: baseDetail?.core?.sim_price,
        compareBase: compareDetail?.core?.priced_at,
        compareSim: compareDetail?.core?.sim_price,
      },
      ...Array.from(pairedByStem.values()),
    ];

    return { paired, positionFields, staticFields };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
            {getDisplayInvestmentCode(row) || investmentCode}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            {baseDate} Sim #{baseSim} vs {compareDate} Sim #{compareSim}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <GVarLoader size={120} sx={{ py: 4 }} />}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && (baseDetail || compareDetail || row) && !expanded && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Field</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "rgba(0,104,66,0.06)", color: "#006842" }}>
                    Base Date ({baseDate})
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "rgba(190,155,83,0.12)", color: "#8a6d2b" }}>
                    Compare Date ({compareDate})
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Δ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryFields.map(({ key, label, format }) => {
                  // Prefer the comparison row's own value: it's already
                  // portfolio-scoped (same endpoint that drives the pair-compare
                  // table), whereas the per-side detail fetch does not honor the
                  // portfolio filter and can show an unfiltered/firmwide figure.
                  const baseVal = row?.[key] ?? baseDetail?.core?.[key];
                  const compareVal = row?.[`compare_${key}`] ?? compareDetail?.core?.[key];
                  // Missing side (e.g. investment doesn't exist on that date) counts as zero,
                  // consistent with the pair-comparison delta calculation.
                  const delta = baseVal != null || compareVal != null ? (baseVal ?? 0) - (compareVal ?? 0) : null;
                  return (
                    <TableRow
                      key={key}
                      hover
                      onClick={() => {
                        if (baseDetail?.drivers?.length > 0 || compareDetail?.drivers?.length > 0) {
                          setExpanded(true);
                        }
                      }}
                      sx={{
                        cursor: (baseDetail?.drivers?.length > 0 || compareDetail?.drivers?.length > 0)
                          ? "pointer"
                          : "default",
                      }}
                    >
                      <TableCell sx={{ border: 0, fontWeight: 600, fontSize: "0.8rem", color: "text.secondary" }}>
                        {label}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 600, bgcolor: "rgba(0,104,66,0.03)" }}>
                        {baseVal != null ? format(baseVal) : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 600, bgcolor: "rgba(190,155,83,0.05)" }}>
                        {compareVal != null ? format(compareVal) : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 600 }}>
                        {delta != null ? format(delta) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {(() => {
              const attr = computePnlAttribution();
              if (!attr.hasData) return null;
              const denom = attr.impliedDelta;
              const pct = (v) => (denom !== 0 ? `${((v / denom) * 100).toFixed(1)}%` : null);
              const mismatch = Math.abs(attr.actualDelta - attr.impliedDelta) > 0.5;
              return (
                <Box
                  sx={{
                    mt: 2.5,
                    pt: 2,
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#006842", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.25 }}>
                    PnL Δ Attribution — Quantity vs Price (Midpoint Method)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                    <AttributionCard
                      label="Quantity Effect"
                      value={attr.midQty}
                      pctLabel={pct(attr.midQty)}
                      accent="#006842"
                    />
                    <AttributionCard
                      label="Price Effect"
                      value={attr.midPrice}
                      pctLabel={pct(attr.midPrice)}
                      accent="#006842"
                    />
                    <AttributionCard
                      label="Total PnL Δ"
                      value={attr.actualDelta}
                      accent="#BE9B53"
                      emphasize
                    />
                  </Box>
                  {mismatch && (
                    <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", mt: 1 }}>
                      Quantity × Price implied Δ ({formatCurrency(attr.impliedDelta)}) is used for the % split above
                      and differs slightly from the Total PnL Δ.
                    </Typography>
                  )}
                </Box>
              );
            })()}

            {(baseDetail?.drivers?.length > 0 || compareDetail?.drivers?.length > 0) && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setExpanded(true)}
                  sx={{ textTransform: "none" }}
                >
                  Show Full Breakdown
                </Button>
              </Box>
            )}
          </>
        )}
        {!loading && !error && expanded && (baseDetail || compareDetail) && (
          <>
            {(() => {
              const { paired, positionFields, staticFields } = pairDrivers();
              return (
                <>
                  <DetailKvSection title="Position" fields={positionFields} />
                  <DetailKvSection title="Static Risk Parameters" fields={staticFields} />

                  <Box
                    sx={{
                      border: "1px solid rgba(0,104,66,0.12)",
                      borderRadius: 1,
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.6,
                        bgcolor: "rgba(0,104,66,0.07)",
                        borderBottom: "1px solid rgba(0,104,66,0.12)",
                      }}
                    >
                      <Typography sx={DETAIL_SECTION_TITLE_SX}>INITIAL vs SIMULATED</Typography>
                    </Box>
                    <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.55, px: 1.25, fontSize: "0.78rem" } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: "#ffffff", zIndex: 3 }}>Parameter</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#e8f2ed", color: "#006842", zIndex: 3 }}>
                            <Box>
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Current Date· INITIAL Value</Typography>
                              <Typography sx={{ fontSize: "0.62rem", opacity: 0.7 }}>{baseDate}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f5edd9", color: "#8a6d2b", zIndex: 3 }}>
                            <Box>
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Compare Date· INITIAL Value </Typography>
                              <Typography sx={{ fontSize: "0.62rem", opacity: 0.7 }}>{compareDate}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#e8f2ed", color: "#006842", zIndex: 3 }}>
                            <Box>
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Current Date· SIMULATED Value</Typography>
                              <Typography sx={{ fontSize: "0.62rem", opacity: 0.7 }}>Sim #{baseSim}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f5edd9", color: "#8a6d2b", zIndex: 3 }}>
                            <Box>
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Compare Date· SIMULATED Value</Typography>
                              <Typography sx={{ fontSize: "0.62rem", opacity: 0.7 }}>Sim #{compareSim}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paired.map(({ label, currentBase, currentSim, compareBase, compareSim }, idx) => (
                          <TableRow key={idx} sx={{ bgcolor: idx % 2 === 0 ? "#fff" : "#f7f9f8" }}>
                            <TableCell sx={{ fontWeight: 600, color: "text.secondary", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                              {label}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600, bgcolor: "rgba(0,104,66,0.03)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                              {formatDriverValue(currentBase)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600, bgcolor: "rgba(190,155,83,0.05)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                              {formatDriverValue(compareBase)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600, bgcolor: "rgba(0,104,66,0.03)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                              {formatDriverValue(currentSim)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600, bgcolor: "rgba(190,155,83,0.05)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                              {formatDriverValue(compareSim)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setExpanded(false)}
                    sx={{ textTransform: "none" }}
                  >
                    Hide Full Breakdown
                  </Button>
                </>
              );
            })()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PairCompareDialog({
  open,
  onClose,
  strategyName,
  baseDate,
  baseSim,
  compareDate,
  compareSim,
  rows,
  strategyType,
  portfolioParam,
}) {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryInvestment, setSummaryInvestment] = useState(null);
  const [summaryRow, setSummaryRow] = useState(null);

  if (!open) return null;

  const handleInvestmentClick = (row) => {
    // Store the actual investment_code_2 for API lookups, but pass display code as well
    setSummaryInvestment(row.investment_code_2);
    setSummaryRow(row);
    setSummaryOpen(true);
  };

  const handleSummaryClose = () => {
    setSummaryOpen(false);
    setSummaryInvestment(null);
    setSummaryRow(null);
  };

  // Rows are a pre-filtered snapshot taken at click time from an isolated
  // compare copy (never shared with the MRT table data reference).
  const safeRows = Array.isArray(rows) ? rows : [];

  const baseVals = safeRows.map((r) => r.pnl).filter((v) => v != null);
  const compareVals = safeRows.map((r) => r.compare_pnl).filter((v) => v != null);
  const baseTotalPnl = baseVals.length ? baseVals.reduce((a, b) => a + b, 0) : null;
  const compareTotalPnl = compareVals.length ? compareVals.reduce((a, b) => a + b, 0) : null;
  // Strategy-level Δ = sum(Base) − sum(Compare), including unpaired positions
  const deltaTotalPnl =
    baseTotalPnl != null || compareTotalPnl != null
      ? (baseTotalPnl || 0) - (compareTotalPnl || 0)
      : null;

  // Stable display order: both-sides first (by |Δ| desc), then base-only, then compare-only
  const sortedRows = [...safeRows].sort((a, b) => {
    const tier = (r) => (r.only_compare ? 2 : r.only_base ? 1 : 0);
    const ta = tier(a);
    const tb = tier(b);
    if (ta !== tb) return ta - tb;
    const da = a.pnl_diff;
    const db = b.pnl_diff;
    if (da == null && db == null) {
      return String(getDisplayInvestmentCode(a)).localeCompare(String(getDisplayInvestmentCode(b)));
    }
    if (da == null) return 1;
    if (db == null) return -1;
    return Math.abs(db) - Math.abs(da);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, pt: 0.25 }}>
            {strategyName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing all {safeRows.length} investment{safeRows.length === 1 ? "" : "s"} in this strategy
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Side-by-side sim identity */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
            gap: 1.5,
            alignItems: "stretch",
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "rgba(0,104,66,0.08)",
              border: "1px solid rgba(0,104,66,0.25)",
            }}
          >
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#006842", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
              Base
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", fontFamily: "monospace", color: "#006842", lineHeight: 1.2 }}>
              Sim #{baseSim}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.35 }}>
              {baseDate}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, fontFamily: "monospace", mt: 1 }}>
              PnL {formatCurrency(baseTotalPnl)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: 0.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "text.secondary" }}>
              vs
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "rgba(190,155,83,0.12)",
              border: "1px solid rgba(190,155,83,0.4)",
            }}
          >
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#8a6d2b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
              Compare
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", fontFamily: "monospace", color: "#8a6d2b", lineHeight: 1.2 }}>
              Sim #{compareSim}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.35 }}>
              {compareDate}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, fontFamily: "monospace", mt: 1 }}>
              PnL {formatCurrency(compareTotalPnl)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label={`Δ PnL: ${formatCurrency(deltaTotalPnl)}`}
            size="small"
            color={deltaTotalPnl == null ? "default" : deltaTotalPnl < 0 ? "error" : "success"}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`${safeRows.filter((r) => !r.only_base && !r.only_compare).length} both sides`}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`${safeRows.filter((r) => r.only_base).length} base only`}
            size="small"
            sx={{ fontWeight: 600, bgcolor: "rgba(0,104,66,0.1)" }}
          />
          <Chip
            label={`${safeRows.filter((r) => r.only_compare).length} compare only`}
            size="small"
            sx={{ fontWeight: 600, bgcolor: "rgba(190,155,83,0.15)" }}
          />
        </Box>

        {safeRows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No positions found for this strategy pair.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: "min(75vh, 720px)", overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#ffffff",
                      zIndex: 3,
                      borderBottom: "2px solid rgba(0,104,66,0.25)",
                    }}
                  >
                    Investment Code
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#e8f2ed",
                      color: "#006842",
                      zIndex: 3,
                      borderBottom: "2px solid rgba(0,104,66,0.25)",
                    }}
                  >
                    Base · Sim #{baseSim}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#f5edd9",
                      color: "#8a6d2b",
                      zIndex: 3,
                      borderBottom: "2px solid rgba(0,104,66,0.25)",
                    }}
                  >
                    Compare · Sim #{compareSim}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#ffffff",
                      zIndex: 3,
                      borderBottom: "2px solid rgba(0,104,66,0.25)",
                    }}
                  >
                    Δ PnL
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row, idx) => {
                  const isDeltaNeg = row.pnl_diff != null && row.pnl_diff < 0;
                  return (
                    <TableRow
                      key={`${getDisplayInvestmentCode(row)}::${idx}`}
                      sx={{
                        opacity: row.only_base || row.only_compare ? 0.85 : 1,
                        bgcolor: row.only_compare
                          ? "rgba(190,155,83,0.06)"
                          : row.only_base
                            ? "rgba(0,104,66,0.04)"
                            : undefined,
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          color: "#006842",
                          fontWeight: 600,
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => handleInvestmentClick(row)}
                      >
                        {getDisplayInvestmentCode(row)}
                        {row.only_base && (
                          <Typography component="span" sx={{ ml: 1, fontSize: "0.65rem", color: "#006842", fontWeight: 700 }}>
                            BASE ONLY
                          </Typography>
                        )}
                        {row.only_compare && (
                          <Typography component="span" sx={{ ml: 1, fontSize: "0.65rem", color: "#8a6d2b", fontWeight: 700 }}>
                            COMPARE ONLY
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.8rem", bgcolor: "rgba(0,104,66,0.03)" }}>
                        {formatCurrency(row.pnl)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.8rem", bgcolor: "rgba(190,155,83,0.05)" }}>
                        {formatCurrency(row.compare_pnl)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: row.pnl_diff == null ? "text.secondary" : isDeltaNeg ? "#d32f2f" : "#2e7d32",
                        }}
                      >
                        {formatCurrency(row.pnl_diff)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <PositionCompareSummary
        open={summaryOpen}
        onClose={handleSummaryClose}
        investmentCode={summaryInvestment}
        baseDate={baseDate}
        baseSim={baseSim}
        compareDate={compareDate}
        compareSim={compareSim}
        strategyType={strategyType}
        portfolioParam={portfolioParam}
        row={summaryRow}
      />
    </Dialog>
  );
}

/** Sum numeric leaf values; return null when every leaf is missing (show —). */
function sumPresent(columnId, leafRows) {
  const vals = leafRows
    .map((r) => r.original?.[columnId] ?? r.getValue?.(columnId))
    .filter((v) => v != null && !Number.isNaN(v));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0);
}

function DeltaCell({ value }) {
  if (value == null || Number.isNaN(value)) {
    return (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.85rem", color: "text.secondary", textAlign: "right" }}>
        —
      </Typography>
    );
  }
  const isNeg = value < 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontFamily: "monospace",
          color: isNeg ? "#d32f2f" : "#2e7d32",
          fontSize: "0.85rem",
        }}
      >
        {formatCurrency(value)}
      </Typography>
    </Box>
  );
}

/**
 * Annotate compare rows with strategy-level tier for sorting:
 * 0 = overlap (both sides), 1 = base-only, 2 = compare-only.
 * Also normalizes pnl_diff so a missing side is treated as zero (instead of
 * leaving it null and under-counting strategy-level delta aggregation).
 * Then sort by tier, then by pnl_diff, then investment code.
 */
function prepareCompareRows(rows) {
  const byStrategy = new Map();
  for (const row of rows) {
    const key = row.strategy_name || "";
    if (!byStrategy.has(key)) byStrategy.set(key, { hasBase: false, hasCompare: false });
    const info = byStrategy.get(key);
    if (row.pnl != null || row.only_base) info.hasBase = true;
    if (row.compare_pnl != null || row.only_compare) info.hasCompare = true;
  }

  const annotated = rows.map((row) => {
    const info = byStrategy.get(row.strategy_name || "") || { hasBase: false, hasCompare: false };
    let _compare_tier = 0;
    if (info.hasBase && info.hasCompare) _compare_tier = 0;
    else if (info.hasBase) _compare_tier = 1;
    else _compare_tier = 2;
    const pnl_diff = (row.pnl ?? 0) - (row.compare_pnl ?? 0);
    return { ...row, _compare_tier, pnl_diff };
  });

  annotated.sort((a, b) => {
    if (a._compare_tier !== b._compare_tier) return a._compare_tier - b._compare_tier;
    if (a.pnl_diff !== b.pnl_diff) return a.pnl_diff - b.pnl_diff;
    return String(a.investment_code_2).localeCompare(String(b.investment_code_2));
  });

  return annotated;
}

/** Leaf originals for a table row (handles grouped strategy rows). */
function leafOriginals(row) {
  if (row?.getIsGrouped?.()) {
    const leaves = typeof row.getLeafRows === "function"
      ? row.getLeafRows()
      : (row.subRows || []);
    return leaves.map((l) => l.original || {}).filter((o) => o && Object.keys(o).length);
  }
  return [row?.original || {}];
}

function sumMetric(leaves, getter) {
  const vals = leaves.map(getter).filter((v) => v != null && !Number.isNaN(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0);
}

/**
 * Compare-mode sort: unpaired strategies (tier ≠ 0, i.e. no comparison
 * counterpart at all) always sort after overlaps — pinned to the bottom
 * regardless of the current asc/desc toggle (`desc` compensates for MRT
 * negating the comparator's return value on a descending sort) — then by
 * the metric (asc/desc handled by MRT). Works for group + leaf rows.
 */
function makeCompareSortingFn(getter, desc) {
  return (rowA, rowB) => {
    const leavesA = leafOriginals(rowA);
    const leavesB = leafOriginals(rowB);
    const tierA = leavesA[0]?._compare_tier ?? 0;
    const tierB = leavesB[0]?._compare_tier ?? 0;
    const unpairedA = tierA !== 0;
    const unpairedB = tierB !== 0;
    if (unpairedA !== unpairedB) {
      const raw = unpairedA ? 1 : -1;
      return desc ? -raw : raw;
    }

    const va = sumMetric(leavesA, getter);
    const vb = sumMetric(leavesB, getter);
    if (va == null && vb == null) {
      const na = String(leavesA[0]?.strategy_name || getDisplayInvestmentCode(leavesA[0]) || "");
      const nb = String(leavesB[0]?.strategy_name || getDisplayInvestmentCode(leavesB[0]) || "");
      return na.localeCompare(nb);
    }
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va !== vb) return va - vb;
    const na = String(leavesA[0]?.strategy_name || getDisplayInvestmentCode(leavesA[0]) || "");
    const nb = String(leavesB[0]?.strategy_name || getDisplayInvestmentCode(leavesB[0]) || "");
    return na.localeCompare(nb);
  };
}

/** Δ at group level = sum(Base) − sum(Compare), matching top-bar total. pnl_diff is pre-normalized (missing side = 0) by prepareCompareRows. */
function makeCompareDeltaSortingFn(desc) {
  return makeCompareSortingFn((r) => r.pnl_diff, desc);
}

function PnlCell({ value }) {
  const isNeg = value < 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontFamily: "monospace",
          color: isNeg ? "#d32f2f" : "#2e7d32",
          fontSize: "0.82rem",
        }}
      >
        {formatCurrency(value)}
      </Typography>
    </Box>
  );
}

function getDynamicColumns(compareEnabled, baseDate, compareDate, sortingDescById = {}) {
  const compareBasePnlSortingFn = makeCompareSortingFn((r) => r.pnl, !!sortingDescById.pnl);
  const compareComparePnlSortingFn = makeCompareSortingFn((r) => r.compare_pnl, !!sortingDescById.compare_pnl);
  const compareDeltaSortingFn = makeCompareDeltaSortingFn(!!sortingDescById.pnl_diff);
  const baseColumns = [
    {
      accessorKey: "strategy_name",
      header: "Strategy Name",
      size: 450,
      minSize: 300,
    },
    {
      accessorKey: "investment_code_2",
      header: "Investment Code",
      size: 150,
      Cell: ({ cell, row, table }) => {
        if (row.getIsGrouped()) return null;
        const displayCode = getDisplayInvestmentCode(row.original);
        return (
          <Typography
            component="span"
            variant="body2"
            onClick={() => {
              if (table.options.meta?.actionsDisabled) return;
              table.options.meta?.openInvestmentDetail(row.original);
            }}
            sx={{
              fontWeight: 600,
              fontSize: "0.85rem",
              color: table.options.meta?.actionsDisabled ? "text.disabled" : "#006842",
              cursor: table.options.meta?.actionsDisabled ? "default" : "pointer",
              "&:hover": table.options.meta?.actionsDisabled
                ? undefined
                : { textDecoration: "underline" },
            }}
          >
            {displayCode}
          </Typography>
        );
      },
    },
    {
      accessorKey: "pnl",
      header: compareEnabled ? `${baseDate} PnL` : "PnL",
      size: 150,
      enableSorting: true,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      aggregationFn: compareEnabled ? sumPresent : "sum",
      AggregatedCell: ({ cell }) => <PnlCell value={cell.getValue()} />,
      Cell: ({ cell }) => <PnlCell value={cell.getValue()} />,
      sortingFn: compareEnabled ? compareBasePnlSortingFn : "basic",
      Header: compareEnabled ? ({ column }) => (
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", lineHeight: 1.2 }}>
            {baseDate} PnL
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", fontWeight: 500 }}>
            Base
          </Typography>
        </Box>
      ) : undefined,
    },
  ];

  if (compareEnabled) {
    baseColumns.push(
      {
        accessorKey: "_compare_tier",
        header: "Tier",
        size: 1,
        enableHiding: true,
        aggregationFn: "min",
      },
      {
        accessorKey: "compare_pnl",
        header: `${compareDate} PnL`,
        size: 150,
        enableSorting: true,
        muiTableHeadCellProps: { align: "right" },
        muiTableBodyCellProps: { align: "right" },
        aggregationFn: sumPresent,
        AggregatedCell: ({ cell }) => <PnlCell value={cell.getValue()} />,
        Cell: ({ cell }) => <PnlCell value={cell.getValue()} />,
        sortingFn: compareComparePnlSortingFn,
        Header: ({ column }) => (
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", lineHeight: 1.2 }}>
              {compareDate} PnL
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", fontWeight: 500 }}>
              Compare
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "pnl_diff",
        header: "Δ PnL",
        size: 150,
        enableSorting: true,
        muiTableHeadCellProps: { align: "right" },
        muiTableBodyCellProps: { align: "right" },
        aggregationFn: sumPresent,
        AggregatedCell: ({ cell }) => <DeltaCell value={cell.getValue()} />,
        Cell: ({ cell }) => <DeltaCell value={cell.getValue()} />,
        sortingFn: compareDeltaSortingFn,
        Header: ({ column }) => (
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", lineHeight: 1.2 }}>
              Δ PnL
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", fontWeight: 500 }}>
              Base − Compare
            </Typography>
          </Box>
        ),
      },
    );
    // In compare mode, only show PnL columns
    return baseColumns;
  }

  // Add remaining columns for non-compare mode
  baseColumns.push(
    {
      accessorKey: "quantity",
      header: "Quantity",
      size: 110,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
          {formatNumber(cell.getValue(), 0)}
        </Typography>
      ),
    },
    {
      accessorKey: "priced_at",
      header: "Priced At",
      size: 110,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
          {formatNumber(cell.getValue(), 2)}
        </Typography>
      ),
    },
    {
      accessorKey: "sim_price",
      header: "Sim Price",
      size: 110,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
          {formatNumber(cell.getValue(), 2)}
        </Typography>
      ),
    },
    {
      accessorKey: "pnl_pct",
      header: "PnL %",
      size: 90,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => {
        const val = cell.getValue();
        if (val == null || isNaN(val)) return <span>—</span>;
        const isNeg = val < 0;
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              color: isNeg ? "#d32f2f" : "#2e7d32",
            }}
          >
            {val >= 0 ? "+" : ""}{val.toFixed(2)}%
          </Typography>
        );
      },
      sortingFn: "basic",
    },
    {
      accessorKey: "multiplier",
      header: "Multiplier",
      size: 90,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
          {formatNumber(cell.getValue(), 2)}
        </Typography>
      ),
    },
    {
      accessorKey: "fx_rate",
      header: "FX Rate",
      size: 90,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
          {formatNumber(cell.getValue(), 4)}
        </Typography>
      ),
    }
  );

  return baseColumns;
}

const columns = [
  {
    accessorKey: "strategy_name",
    header: "Strategy Name",
    size: 450,
    minSize: 300,
  },
  {
    accessorKey: "investment_code_2",
    header: "Investment Code",
    size: 150,
    Cell: ({ cell, row, table }) => {
      if (row.getIsGrouped()) return null;
      const displayCode = getDisplayInvestmentCode(row.original);
      return (
        <Typography
          component="span"
          variant="body2"
          onClick={() => table.options.meta?.openInvestmentDetail(row.original)}
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            color: "#006842",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {displayCode}
        </Typography>
      );
    },
  },
  {
    accessorKey: "pnl",
    header: "PnL",
    size: 150,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    aggregationFn: "sum",
    AggregatedCell: ({ cell }) => <PnlCell value={cell.getValue()} />,
    Cell: ({ cell }) => <PnlCell value={cell.getValue()} />,
    sortingFn: "basic",
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    size: 110,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 0)}
      </Typography>
    ),
  },
  {
    accessorKey: "priced_at",
    header: "Current Price",
    size: 110,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 4)}
      </Typography>
    ),
  },
  {
    accessorKey: "sim_price",
    header: "Sim Price",
    size: 110,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 4)}
      </Typography>
    ),
  },
  {
    id: "price_change",
    header: "Price Chg %",
    size: 130,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    accessorFn: (row) => {
      if (!row.priced_at || row.priced_at === 0) return null;
      return ((row.sim_price - row.priced_at) / row.priced_at) * 100;
    },
    Cell: ({ cell }) => {
      const val = cell.getValue();
      if (val == null) return "—";
      const isNeg = val < 0;
      return (
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: isNeg ? "#d32f2f" : "#2e7d32",
          }}
        >
          {val >= 0 ? "+" : ""}{val.toFixed(2)}%
        </Typography>
      );
    },
    sortingFn: "basic",
  },
  {
    accessorKey: "multiplier",
    header: "Multiplier",
    size: 90,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 2)}
      </Typography>
    ),
  },
  {
    accessorKey: "fx_rate",
    header: "FX Rate",
    size: 90,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 4)}
      </Typography>
    ),
  },
];

// Keep original columns for backwards compat when not using dynamic columns
const legacyColumns = [
  {
    accessorKey: "strategy_name",
    header: "Strategy Name",
    size: 450,
    minSize: 300,
  },
  {
    accessorKey: "investment_code_2",
    header: "Investment Code",
    size: 150,
    Cell: ({ cell, row, table }) => {
      if (row.getIsGrouped()) return null;
      const displayCode = getDisplayInvestmentCode(row.original);
      return (
        <Typography
          component="span"
          variant="body2"
          onClick={() => table.options.meta?.openInvestmentDetail(row.original)}
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            color: "#006842",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {displayCode}
        </Typography>
      );
    },
  },
  {
    accessorKey: "pnl",
    header: "PnL",
    size: 150,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    aggregationFn: "sum",
    AggregatedCell: ({ cell }) => <PnlCell value={cell.getValue()} />,
    Cell: ({ cell }) => <PnlCell value={cell.getValue()} />,
    sortingFn: "basic",
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    size: 110,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 0)}
      </Typography>
    ),
  },
  {
    accessorKey: "priced_at",
    header: "Priced At",
    size: 110,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 2)}
      </Typography>
    ),
  },
  {
    accessorKey: "sim_price",
    header: "Sim Price",
    size: 110,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 2)}
      </Typography>
    ),
  },
  {
    accessorKey: "pnl_pct",
    header: "PnL %",
    size: 90,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => {
      const val = cell.getValue();
      if (val == null || isNaN(val)) return <span>—</span>;
      const isNeg = val < 0;
      return (
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.8rem",
            color: isNeg ? "#d32f2f" : "#2e7d32",
          }}
        >
          {val >= 0 ? "+" : ""}{val.toFixed(2)}%
        </Typography>
      );
    },
    sortingFn: "basic",
  },
  {
    accessorKey: "multiplier",
    header: "Multiplier",
    size: 90,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 2)}
      </Typography>
    ),
  },
  {
    accessorKey: "fx_rate",
    header: "FX Rate",
    size: 90,
    muiTableHeadCellProps: { align: "right" },
    muiTableBodyCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {formatNumber(cell.getValue(), 4)}
      </Typography>
    ),
  },
];

const SIM_POSITIONS_EXPORT_COLUMNS = [
  { header: "strategy_name", accessorKey: "strategy_name" },
  { header: "investment_code", getValue: (row) => getDisplayInvestmentCode(row) },
  { header: "investment_type_code", accessorKey: "investment_type_code" },
  { header: "priced_currency", accessorKey: "priced_currency" },
  { header: "quantity", accessorKey: "quantity" },
  { header: "priced_at", accessorKey: "priced_at" },
  { header: "simulation_price", accessorKey: "sim_price" },
  { header: "multiplier", accessorKey: "multiplier" },
  { header: "fx_rate", accessorKey: "fx_rate" },
  { header: "pnl", accessorKey: "pnl" },
];

function csvCell(value) {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

/** Export all positions for the current sim/strategy to a CSV file. */
function exportSimPositionsToCsv(rows, filename) {
  const lines = [
    SIM_POSITIONS_EXPORT_COLUMNS.map((c) => csvCell(c.header)).join(","),
    ...rows.map((row) =>
      SIM_POSITIONS_EXPORT_COLUMNS
        .map((c) => csvCell(c.getValue ? c.getValue(row) : row[c.accessorKey]))
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([lines], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename.replace(/\s+/g, "_")}.csv`;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function RiskMonitorInvestments() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const date = searchParams.get("date");
  const simNumber = searchParams.get("sim");
  const strategyType = searchParams.get("strategy");
  const portfolioParam = searchParams.get("portfolio");
  // "greeks" when opened from the Portfolio Greeks view; sim numbers there are not MC sims.
  const simSource = searchParams.get("source") || "simulation";
  const isGreeksSource = simSource === "greeks";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([{ id: "pnl", desc: false }]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [factors, setFactors] = useState(null);
  const [scenario, setScenario] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailInvestmentCode, setDetailInvestmentCode] = useState(null);
  const [detailSourceRow, setDetailSourceRow] = useState(null);

  // Base (date, sim) editor — lets the "sim details" context itself be
  // repointed at any date/sim, not just the compare side, so any two
  // (date, sim) pairs can be put side by side.
  const [baseEditorOpen, setBaseEditorOpen] = useState(false);
  const [baseAvailableDates, setBaseAvailableDates] = useState([]);
  const [baseAvailableSims, setBaseAvailableSims] = useState([]);
  const [pendingBaseDate, setPendingBaseDate] = useState(date);
  const [pendingBaseSim, setPendingBaseSim] = useState(
    simNumber != null ? { sim_number: Number(simNumber), label: null } : null
  );

  // Comparison state
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareDate, setCompareDate] = useState(null);
  const [compareSim, setCompareSim] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareApplied, setCompareApplied] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSims, setAvailableSims] = useState([]);
  const [pairDialogOpen, setPairDialogOpen] = useState(false);
  const [pairDialogStrategy, setPairDialogStrategy] = useState(null);
  const [pairDialogRows, setPairDialogRows] = useState([]);
  // Full comparison payload kept isolated from MRT `data` (separate object
  // copies). MRT grouping previously mutated/shared refs and under-counted
  // investments in the popup (e.g. 8 or 11 instead of all 18).
  const [compareAllRows, setCompareAllRows] = useState([]);
  const compareSnapshotRef = useRef([]);
  const detailAbortControllerRef = useRef(null);

  const normalizeStrategyKey = useCallback((name) => {
    if (name == null) return "";
    return String(name).trim().toLowerCase();
  }, []);

  const clearCompareSnapshot = useCallback(() => {
    compareSnapshotRef.current = [];
    setCompareAllRows([]);
  }, []);

  useEffect(() => {
    if (!date || !simNumber || !strategyType) {
      setError("Missing required parameters (date, sim, strategy)");
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);
    setFactors(null);
    setScenario(null);

    const investmentPromise = fetchSimInvestmentBreakdown(
      date, Number(simNumber), strategyType, portfolioParam, { signal, source: simSource },
    );
    const factorPromise = isGreeksSource
      ? Promise.resolve(null)
      : fetchSimFactors(date, Number(simNumber), { signal }).catch(() => null);

    Promise.allSettled([investmentPromise, factorPromise]).then(([invRes, facRes]) => {
      if (signal.aborted) return;
      if (invRes.status === "fulfilled") {
        setData(invRes.value?.data || []);
      } else {
        setError(invRes.reason?.message || "Failed to load investment data");
      }
      if (facRes.status === "fulfilled" && facRes.value) {
        const f = facRes.value.factors;
        setFactors(f && Object.keys(f).length > 0 ? f : null);
        if (facRes.value.scenario) setScenario(facRes.value.scenario);
      }
      setLoading(false);
    });

    return () => { controller.abort(); };
  }, [date, simNumber, strategyType, portfolioParam, simSource, isGreeksSource]);

  // Load available dates when compare is toggled
  useEffect(() => {
    if (!compareEnabled) return;
    let cancelled = false;
    fetchAvailableDates()
      .then((dates) => {
        if (cancelled) return;
        // Filter out current date and sort newest first
        const filtered = dates.filter((d) => d !== date).reverse();
        setAvailableDates(filtered);
        // Default to previous date
        if (filtered.length > 0 && !compareDate) {
          const allSorted = [...dates].sort();
          const currentIdx = allSorted.indexOf(date);
          if (currentIdx > 0) {
            setCompareDate(allSorted[currentIdx - 1]);
          } else if (filtered.length > 0) {
            setCompareDate(filtered[0]);
          }
        }
      })
      .catch((err) => console.error("Failed to fetch available dates:", err));
    return () => { cancelled = true; };
  }, [compareEnabled, date, compareDate]);

  // Load available sims when compare date changes; always reset sim on date change
  useEffect(() => {
    if (!compareEnabled || !compareDate) return;
    let cancelled = false;
    setAvailableSims([]);
    setCompareSim(null);
    setCompareApplied(false);
    clearCompareSnapshot();
    fetchAvailableSims(compareDate)
      .then((res) => {
        if (cancelled) return;
        const sims = res.sims || [];
        setAvailableSims(sims);
        // Prefill sim selection if base sim exists on compare date (still requires Start)
        const simNum = Number(simNumber);
        const match = sims.find((s) => s.sim_number === simNum);
        if (match) {
          setCompareSim(match);
        }
      })
      .catch((err) => console.error("Failed to fetch available sims:", err));
    return () => { cancelled = true; };
    // Intentionally omit compareSim — date change always resets it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareEnabled, compareDate, simNumber]);

  // Keep the base editor's pending fields in sync with the URL (covers Apply,
  // browser back/forward, and navigating here fresh from a different sim).
  useEffect(() => {
    setBaseEditorOpen(false);
    setPendingBaseDate(date);
    setPendingBaseSim(simNumber != null ? { sim_number: Number(simNumber), label: null } : null);
  }, [date, simNumber]);

  useEffect(() => {
    if (!baseEditorOpen || baseAvailableDates.length > 0) return;
    fetchAvailableDates()
      .then((dates) => setBaseAvailableDates([...dates].sort().reverse()))
      .catch((err) => console.error("Failed to fetch available dates:", err));
  }, [baseEditorOpen, baseAvailableDates.length]);

  useEffect(() => {
    if (!baseEditorOpen || !pendingBaseDate) return;
    let cancelled = false;
    fetchAvailableSims(pendingBaseDate)
      .then((res) => {
        if (cancelled) return;
        setBaseAvailableSims(res.sims || []);
      })
      .catch((err) => console.error("Failed to fetch available sims:", err));
    return () => { cancelled = true; };
  }, [baseEditorOpen, pendingBaseDate]);

  const handleApplyBaseChange = useCallback(() => {
    const pendingSimNumber = typeof pendingBaseSim === "object" && pendingBaseSim != null
      ? pendingBaseSim.sim_number
      : pendingBaseSim;
    if (!pendingBaseDate || pendingSimNumber == null) return;
    const params = new URLSearchParams({
      date: pendingBaseDate,
      sim: pendingSimNumber,
      strategy: strategyType || "",
    });
    if (portfolioParam) params.set("portfolio", portfolioParam);
    setBaseEditorOpen(false);
    navigate(`/risk-monitor/investments?${params.toString()}`);
  }, [pendingBaseDate, pendingBaseSim, strategyType, portfolioParam, navigate]);

  const reloadBaseData = useCallback(() => {
    if (!date || !simNumber || !strategyType) return;
    setLoading(true);
    fetchSimInvestmentBreakdown(date, Number(simNumber), strategyType, portfolioParam, { source: simSource })
      .then((res) => setData(res.data || []))
      .catch((err) => setError(err.message || "Failed to reload data"))
      .finally(() => setLoading(false));
  }, [date, simNumber, strategyType, portfolioParam, simSource]);

  const runComparison = useCallback(() => {
    const compareSimNumber = typeof compareSim === "object" && compareSim != null
      ? compareSim.sim_number
      : compareSim;
    if (!date || !simNumber || !strategyType || !compareDate || compareSimNumber == null) return;

    setCompareLoading(true);
    setExpanded({});
    setError(null);
    fetchSimInvestmentComparison(
      date,
      Number(simNumber),
      strategyType,
      compareDate,
      Number(compareSimNumber),
      portfolioParam,
    )
      .then((res) => {
        const rows = prepareCompareRows(res.data || []);
        // Fully detach popup snapshot from table data (JSON clone) so MRT
        // grouping/sorting cannot mutate or truncate what the dialog shows.
        const snapshot = JSON.parse(JSON.stringify(rows));
        const tableRows = JSON.parse(JSON.stringify(rows));
        compareSnapshotRef.current = snapshot;
        setCompareAllRows(snapshot);
        setData(tableRows);
        setExpanded({});
        setCompareApplied(true);
        setCompareLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch comparison:", err);
        setError("Failed to load comparison data");
        setCompareApplied(false);
        clearCompareSnapshot();
        setCompareLoading(false);
      });
  }, [date, simNumber, strategyType, compareDate, compareSim, portfolioParam, clearCompareSnapshot]);

  const totalPnl = data.reduce((sum, r) => sum + (r.pnl || 0), 0);
  
  // Compare mode: compute Base, Compare, and Δ totals
  const totalBasePnl = compareApplied
    ? data.reduce((sum, r) => sum + (r.pnl || 0), 0)
    : null;
  const totalComparePnl = compareApplied
    ? data.reduce((sum, r) => sum + (r.compare_pnl || 0), 0)
    : null;
  const totalDelta = compareApplied && (totalBasePnl != null || totalComparePnl != null)
    ? (totalBasePnl || 0) - (totalComparePnl || 0)
    : null;

  const [exporting, setExporting] = useState(false);

  const needsPositionEnrichment = (row) =>
    row.priced_currency == null || row.multiplier == null || row.fx_rate == null;

  const handleExportPositions = useCallback(async () => {
    if (!date || !simNumber || !strategyType) return;
    setExporting(true);
    try {
      let rows = data;
      if (rows.some(needsPositionEnrichment)) {
        rows = await Promise.all(
          rows.map(async (row) => {
            if (!needsPositionEnrichment(row)) return row;
            try {
              const detail = await fetchSimInvestmentDetail(
                date,
                Number(simNumber),
                row.investment_code_2,
                strategyType,
                portfolioParam,
                {},
                row.strategy_name,
                simSource,
              );
              return {
                ...row,
                priced_currency: row.priced_currency ?? detail?.core?.priced_currency,
                multiplier: row.multiplier ?? detail?.core?.multiplier,
                fx_rate: row.fx_rate ?? detail?.core?.fx_rate,
              };
            } catch (err) {
              return row;
            }
          }),
        );
      }
      exportSimPositionsToCsv(rows, `sim_${simNumber}_${strategyType}_positions_${date}`);
    } finally {
      setExporting(false);
    }
  }, [data, date, simNumber, strategyType, portfolioParam, simSource]);

  const openInvestmentDetail = useCallback(
    (investmentRow) => {
      if (!investmentRow || !date || !simNumber || !strategyType) return;

      detailAbortControllerRef.current?.abort();
      const controller = new AbortController();
      detailAbortControllerRef.current = controller;

      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError(null);
      setDetailData(null);
      setDetailInvestmentCode(investmentRow.investment_code_2);
      setDetailSourceRow(investmentRow);

      fetchSimInvestmentDetail(
        date,
        Number(simNumber),
        investmentRow.investment_code_2,
        strategyType,
        portfolioParam,
        { signal: controller.signal },
        investmentRow.strategy_name,
        simSource,
      )
        .then((res) => {
          if (controller.signal.aborted) return;
          setDetailData(res);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          setDetailError(
            err?.response?.data?.detail || err.message || "Failed to load investment detail",
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) setDetailLoading(false);
        });
    },
    [date, simNumber, strategyType, portfolioParam, simSource],
  );

  const closeInvestmentDetail = useCallback(() => {
    detailAbortControllerRef.current?.abort();
    setDetailOpen(false);
  }, []);

  const handleToggleCompare = () => {
    if (compareLoading) return;
    const next = !compareEnabled;
    setCompareEnabled(next);
    if (next) {
      setExpanded({});
      setCompareApplied(false);
      clearCompareSnapshot();
      setBaseEditorOpen(true);
    } else {
      // Clearing compare mode - reload base data
      setCompareDate(null);
      setCompareSim(null);
      setCompareLoading(false);
      setCompareApplied(false);
      clearCompareSnapshot();
      setPairDialogOpen(false);
      setPairDialogStrategy(null);
      setPairDialogRows([]);
      setExpanded({});
      setBaseEditorOpen(false);
      reloadBaseData();
    }
  };

  const handlePairDialogClose = () => {
    setPairDialogOpen(false);
    setPairDialogStrategy(null);
    setPairDialogRows([]);
  };

  const countForStrategy = useCallback((strategyName) => {
    const key = normalizeStrategyKey(strategyName);
    return compareSnapshotRef.current.filter(
      (r) => normalizeStrategyKey(r.strategy_name) === key,
    ).length;
  }, [compareAllRows, normalizeStrategyKey]);

  const handleGroupRowClick = useCallback(
    (row) => {
      if (compareLoading) return;
      if (compareApplied && row.getIsGrouped()) {
        const key = normalizeStrategyKey(row.groupingValue);
        const filtered = compareSnapshotRef.current
          .filter((r) => normalizeStrategyKey(r.strategy_name) === key)
          .map((r) => ({ ...r }));
        setPairDialogRows(filtered);
        setPairDialogStrategy(row.groupingValue);
        setPairDialogOpen(true);
      } else if (!compareApplied) {
        row.getToggleExpandedHandler()();
      }
    },
    [compareApplied, compareLoading, normalizeStrategyKey]
  );

  const tableMeta = useMemo(
    () => ({
      openInvestmentDetail,
      actionsDisabled: compareLoading,
      countForStrategy,
    }),
    [openInvestmentDetail, compareLoading, countForStrategy],
  );

  const sortingDescById = useMemo(
    () => Object.fromEntries(sorting.map((s) => [s.id, !!s.desc])),
    [sorting],
  );
  const activeColumns = useMemo(
    () => getDynamicColumns(compareApplied, date, compareDate, sortingDescById),
    [compareApplied, date, compareDate, sortingDescById],
  );

  // Reset sort when entering/leaving compare mode so header clicks work from a known default
  useEffect(() => {
    setSorting(
      compareApplied
        ? [{ id: "pnl_diff", desc: false }]
        : [{ id: "pnl", desc: false }],
    );
  }, [compareApplied]);

  const table = useMaterialReactTable({
    columns: activeColumns,
    data,
    meta: tableMeta,
    enableColumnResizing: !compareLoading,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: !compareLoading,
    enableMultiSort: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableExpanding: !compareApplied,
    groupedColumnMode: "remove",
    initialState: {
      grouping: ["strategy_name"],
      density: "compact",
      expanded: {},
      columnVisibility: { _compare_tier: false },
    },
    state: {
      expanded: compareApplied ? {} : expanded,
      grouping: ["strategy_name"],
      sorting,
      columnVisibility: { _compare_tier: false },
      isLoading: compareLoading,
    },
    onSortingChange: compareLoading ? undefined : setSorting,
    enableGroupRemove: false,
    onExpandedChange: compareApplied || compareLoading ? undefined : setExpanded,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        size: 500,
        minSize: 350,
        Cell: ({ row, table }) =>
          row.getIsGrouped() ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                pl: `${row.depth * 2}rem`,
                cursor: compareLoading ? "wait" : "pointer",
                width: "100%",
                minWidth: 0,
                opacity: compareLoading ? 0.6 : 1,
                pointerEvents: compareLoading ? "none" : "auto",
              }}
              onClick={() => handleGroupRowClick(row)}
            >
              {!compareApplied && (
                row.getIsExpanded() ? (
                  <RemoveIcon sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0, mr: 0.75 }} />
                ) : (
                  <AddIcon sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0, mr: 0.75 }} />
                )
              )}
              <Tooltip
                title={compareApplied ? `Open pair comparison: ${row.groupingValue}` : row.groupingValue}
                arrow
                enterDelay={300}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    color: compareApplied ? "#006842" : "text.primary",
                    minWidth: 0,
                    flex: 1,
                    ...(compareApplied ? { "&:hover": { textDecoration: "underline" } } : {}),
                  }}
                >
                  {row.groupingValue}
                </Typography>
              </Tooltip>
              <Chip
                label={
                  compareApplied
                    ? (table.options.meta?.countForStrategy?.(row.groupingValue) ?? 0)
                    : (row.subRows?.length || 0)
                }
                size="small"
                sx={{
                  height: 18,
                  minWidth: 18,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor: "rgba(0,104,66,0.1)",
                  color: "#006842",
                  ml: "auto",
                  flexShrink: 0,
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            </Box>
          ) : null,
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 220px)",
        position: "relative",
        ...(compareLoading
          ? {
              pointerEvents: "none",
              opacity: 0.55,
            }
          : {}),
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#006842",
        bgcolor: "#e8f2ed",
        borderBottom: "2px solid #006842",
        borderRight: "1px solid rgba(0,104,66,0.08)",
        py: 0.75,
        textAlign: "center",
        zIndex: 3,
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "center",
        },
        "& .Mui-TableHeadCell-Content-Labels": {
          overflow: "visible !important",
        },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "normal !important",
          overflow: "visible !important",
          textOverflow: "unset !important",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.8rem",
        borderRight: "1px solid #f0f0f0",
        py: 0.5,
        color: "text.primary",
      },
    },
    muiTableBodyRowProps: ({ row, table, staticRowIndex }) => {
      if (row.getIsGrouped()) {
        const visibleRows = table.getRowModel().rows;
        let groupCount = 0;
        for (const r of visibleRows) {
          if (r.id === row.id) break;
          if (r.getIsGrouped()) groupCount++;
        }
        return {
          sx: {
            bgcolor: groupCount % 2 === 0 ? "#f5f3ef" : "#ece9e3",
            borderTop: "1px solid #ddd",
            "&:hover": { bgcolor: "#e5e2db" },
          },
        };
      }
      const parentRow = row.getParentRow?.();
      const siblingIndex = parentRow
        ? parentRow.subRows.findIndex((r) => r.id === row.id)
        : staticRowIndex;
      return {
        sx: {
          bgcolor: siblingIndex % 2 === 0 ? "#ffffff" : "#f2f7f5",
          "&:hover": { bgcolor: "#eef2f0" },
        },
      };
    },
  });

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%" }}>
      {/* Breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <Tooltip title="Back to G-VaR">
          <Box
            onClick={() => navigate("/g-var")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "#006842",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </Box>
        </Tooltip>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ minWidth: 0 }}>
          <MuiLink
            component={Link}
            to="/g-var"
            underline="hover"
            sx={{ color: "#006842", fontWeight: 600, fontSize: "0.9rem" }}
          >
            G-VaR
          </MuiLink>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#BE9B53" }}>
            {strategyType || "Strategy"}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "text.primary" }}>
            Sim #{simNumber}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Date / Sim chips under Sim # header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Chip label={`Date: ${date}`} size="small" sx={{ bgcolor: "rgba(0,104,66,0.1)", fontWeight: 600 }} />
        <Chip label={`Sim #${simNumber}`} size="small" sx={{ bgcolor: "#BE9B53", color: "white", fontWeight: 600 }} />
        <Chip label={strategyType} size="small" sx={{ bgcolor: "#006842", color: "white", fontWeight: 600 }} />
        {portfolioParam && (
          <Chip label={`Portfolio: ${portfolioParam}`} size="small" sx={{ bgcolor: "#91B6C0", color: "white" }} />
        )}
        {!loading && data.length > 0 && (
          <Chip
            label={`Total PnL: ${formatCurrency(totalPnl)}`}
            size="small"
            sx={{ bgcolor: totalPnl < 0 ? "rgba(197,48,48,0.1)" : "rgba(0,104,66,0.1)", fontWeight: 700 }}
          />
        )}
        {!isGreeksSource && (
          <Tooltip title="Change date / sim">
            <IconButton
              size="small"
              onClick={() => setBaseEditorOpen((v) => !v)}
              sx={{ color: baseEditorOpen ? "#006842" : "text.disabled" }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!loading && data.length > 0 && !compareApplied && (
          <Button
            size="small"
            variant="outlined"
            startIcon={
              exporting ? (
                <CircularProgress size={14} sx={{ color: "#006842" }} />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            onClick={handleExportPositions}
            disabled={exporting}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderColor: "#006842",
              color: "#006842",
              "&:hover": { borderColor: "#005537", bgcolor: "rgba(0,104,66,0.06)" },
            }}
          >
            {exporting ? "Exporting…" : "Export"}
          </Button>
        )}
      </Box>

      {/* Base date/sim editor — repoints this sim-details view at any (date, sim) */}
      {baseEditorOpen && !isGreeksSource && (
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            bgcolor: "#f4f8f6",
            borderRadius: 1.5,
            border: "1px solid rgba(0,104,66,0.2)",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140, width: { xs: "100%", sm: 150 } }}>
            <InputLabel>Date</InputLabel>
            <Select
              value={pendingBaseDate || ""}
              label="Date"
              onChange={(e) => {
                setPendingBaseDate(e.target.value);
                setPendingBaseSim(null);
                setBaseAvailableSims([]);
              }}
            >
              {baseAvailableDates.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            key={pendingBaseDate}
            size="small"
            sx={{ minWidth: 180, flex: { sm: "1 1 220px" }, maxWidth: 320 }}
            disabled={!pendingBaseDate}
            value={pendingBaseSim}
            onChange={(_, newValue) => {
              if (typeof newValue === "string") {
                const num = Number(newValue);
                if (!Number.isNaN(num)) {
                  const match = baseAvailableSims.find((s) => s.sim_number === num);
                  setPendingBaseSim(match || { sim_number: num, label: null, scenario_type: "mc" });
                } else {
                  setPendingBaseSim(null);
                }
              } else {
                setPendingBaseSim(newValue);
              }
            }}
            options={baseAvailableSims}
            getOptionLabel={(opt) => {
              if (opt == null) return "";
              if (typeof opt === "number" || typeof opt === "string") return String(opt);
              return opt.label ? `${opt.sim_number} — ${opt.label}` : String(opt.sim_number);
            }}
            isOptionEqualToValue={(opt, val) => {
              const a = typeof opt === "object" ? opt.sim_number : opt;
              const b = typeof val === "object" ? val?.sim_number : val;
              return a === b;
            }}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return options;
              return options.filter((s) => {
                const num = String(s.sim_number);
                const label = (s.label || "").toLowerCase();
                return num.includes(q) || label.includes(q);
              });
            }}
            freeSolo
            autoHighlight
            clearOnBlur={false}
            renderInput={(params) => <TextField {...params} label="Sim" placeholder="Search…" />}
          />

          <Button
            size="small"
            variant="contained"
            onClick={handleApplyBaseChange}
            disabled={!pendingBaseDate || pendingBaseSim == null}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              flexShrink: 0,
              bgcolor: "#006842",
              "&:hover": { bgcolor: "#005537" },
            }}
          >
            Go
          </Button>
        </Box>
      )}

      {/* Compare CTA below date/sim chips */}
      {!compareEnabled && !isGreeksSource && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<CompareArrowsIcon />}
            onClick={handleToggleCompare}
            disabled={compareLoading}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              px: 2.5,
              py: 0.75,
              boxShadow: "0 2px 8px rgba(0,104,66,0.35)",
              bgcolor: "#006842",
              "&:hover": {
                bgcolor: "#005537",
                boxShadow: "0 3px 12px rgba(0,104,66,0.45)",
              },
            }}
          >
            Compare
          </Button>
        </Box>
      )}

      {/* Compact horizontal compare bar — date/sim first, exit last */}
      {compareEnabled && (
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            bgcolor: "#f4f8f6",
            borderRadius: 1.5,
            border: "1px solid rgba(0,104,66,0.2)",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "#006842",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              flexShrink: 0,
              display: { xs: "none", sm: "block" },
            }}
          >
            vs
          </Typography>

          <FormControl size="small" sx={{ minWidth: 140, width: { xs: "100%", sm: 150 } }} disabled={compareLoading}>
            <InputLabel>Date</InputLabel>
            <Select
              value={compareDate || ""}
              label="Date"
              onChange={(e) => {
                const wasApplied = compareApplied;
                setCompareSim(null);
                setCompareApplied(false);
                clearCompareSnapshot();
                setCompareDate(e.target.value);
                if (wasApplied) reloadBaseData();
              }}
            >
              {availableDates.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            key={compareDate}
            size="small"
            sx={{ minWidth: 180, flex: { sm: "1 1 220px" }, maxWidth: 320 }}
            disabled={compareLoading || !compareDate}
            value={compareSim}
            onChange={(_, newValue) => {
              const wasApplied = compareApplied;
              setCompareApplied(false);
              clearCompareSnapshot();
              if (typeof newValue === "string") {
                const num = Number(newValue);
                if (!Number.isNaN(num)) {
                  const match = availableSims.find((s) => s.sim_number === num);
                  setCompareSim(match || { sim_number: num, label: null, scenario_type: "mc" });
                } else {
                  setCompareSim(null);
                }
              } else {
                setCompareSim(newValue);
              }
              if (wasApplied) reloadBaseData();
            }}
            options={availableSims}
            getOptionLabel={(opt) => {
              if (opt == null) return "";
              if (typeof opt === "number" || typeof opt === "string") return String(opt);
              return opt.label
                ? `${opt.sim_number} — ${opt.label}`
                : String(opt.sim_number);
            }}
            isOptionEqualToValue={(opt, val) => {
              const a = typeof opt === "object" ? opt.sim_number : opt;
              const b = typeof val === "object" ? val?.sim_number : val;
              return a === b;
            }}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return options;
              return options.filter((s) => {
                const num = String(s.sim_number);
                const label = (s.label || "").toLowerCase();
                return num.includes(q) || label.includes(q);
              });
            }}
            freeSolo
            autoHighlight
            clearOnBlur={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sim"
                placeholder="Search…"
              />
            )}
          />

          <Button
            size="small"
            variant="contained"
            onClick={runComparison}
            disabled={
              compareLoading
              || !compareDate
              || compareSim == null
              || (compareApplied && !compareLoading)
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              flexShrink: 0,
              bgcolor: "#BE9B53",
              color: "#1a1a1a",
              "&:hover": { bgcolor: "#a8873f" },
              "&.Mui-disabled": {
                bgcolor: compareApplied ? "rgba(190,155,83,0.45)" : undefined,
                color: compareApplied ? "#1a1a1a" : undefined,
              },
            }}
          >
            {compareApplied ? "Compared" : "Start Compare"}
          </Button>

          {compareApplied && !compareLoading && (
            <>
              <Chip
                label={`Base PnL: ${formatCurrency(totalBasePnl)}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  flexShrink: 0,
                  bgcolor: "rgba(0,104,66,0.12)",
                  color: "#006842",
                }}
              />
              <Chip
                label={`Compare PnL: ${formatCurrency(totalComparePnl)}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  flexShrink: 0,
                  bgcolor: "rgba(190,155,83,0.15)",
                  color: "#8a6d2b",
                }}
              />
              <Chip
                label={`Δ ${formatCurrency(totalDelta)}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  flexShrink: 0,
                  bgcolor: totalDelta == null
                    ? "rgba(0,0,0,0.06)"
                    : totalDelta < 0
                      ? "rgba(197,48,48,0.15)"
                      : "rgba(0,104,66,0.15)",
                  color: totalDelta == null
                    ? "text.secondary"
                    : totalDelta < 0
                      ? "#d32f2f"
                      : "#2e7d32",
                }}
              />
            </>
          )}

          {compareLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <CircularProgress size={18} sx={{ color: "#006842" }} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            size="small"
            startIcon={<CompareArrowsIcon />}
            onClick={handleToggleCompare}
            disabled={compareLoading}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              flexShrink: 0,
              ml: { sm: "auto" },
              bgcolor: "#c62828",
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            Exit
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <GVarLoader />
      ) : data.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">No investment data found</Typography>
        </Box>
      ) : (
        <>
          {!factors && scenario && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1.5,
                px: 2,
                py: 1,
                bgcolor: "#006842",
                borderRadius: 1,
                color: "white",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {scenario.scenario_type === "custom" ? "Custom Shock" : "Historical Shock"}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  {scenario.scenario_name}
                </Typography>
              </Box>
              <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 2 }}>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {scenario.scenario_type === "custom" ? "Definition" : "Stress Period"}
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
                  {scenario.stress_period}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: factors ? { xs: "1fr", md: "260px 1fr", lg: "300px 1fr" } : "1fr",
              gap: { xs: 1.5, md: 2 },
              alignItems: "start",
            }}
          >
            {factors && <FactorPanel factors={factors} />}
            <Box sx={{ overflow: "auto", minWidth: 0 }}>
              <MaterialReactTable table={table} />
            </Box>
          </Box>
        </>
      )}

      <InvestmentDetailDialog
        open={detailOpen}
        onClose={closeInvestmentDetail}
        loading={detailLoading}
        error={detailError}
        data={detailData}
        investmentCode={detailInvestmentCode}
        strategyType={strategyType}
        simNumber={simNumber}
        sourceRow={detailSourceRow}
      />

      <PairCompareDialog
        open={pairDialogOpen}
        onClose={handlePairDialogClose}
        strategyName={pairDialogStrategy}
        baseDate={date}
        baseSim={simNumber}
        compareDate={compareDate}
        compareSim={typeof compareSim === "object" && compareSim != null ? compareSim.sim_number : compareSim}
        rows={pairDialogRows}
        strategyType={strategyType}
        portfolioParam={portfolioParam}
      />
    </Box>
  );
}
